import { compose, filter } from 'ramda';
import { XPathEngine } from '../../../Manifest/formsviewer.domain';
import ManifestParser from '../../../Manifest/modules/manifest.parser';
import { buildNamespaceManager } from '../../utility/xmlHelpers';
import { getDocumentElement } from '../../xmlUtility';
import constants from './../constants';
import { buildTemplateNotFoundErrorMessage } from '../../utility/errorCreators';

const $ = window.jQuery;
const util = window.qd.util;

// TODO: Get this value from some shared location
const ATTRIBUTE_NODE = 2;

function createTemplate(api, files, instanceId, templateInfo) {
    // templateFiles is Object[String -> String] indexed on file names
    let templateFiles = {};

    files.forEach(function (fileItem) {
        templateFiles[fileItem.Name] = fileItem.Contents;
    });

    const manifestStr = templateFiles['manifest.xsf'];
    const manifestDom = $.parseXML(manifestStr);
    const manifestParser = new ManifestParser();
    const xpathEngine = new XPathEngine();

    let defaultView;

    function getTemplateName() {
        if (api.getAccessType() === 'templateName') {
            return api.getTemplateLocation();
        }

        return null;
    }

    function getLastUpdatedDate() {
        const lastUpdatedDate = templateInfo && templateInfo.LastUpdatedDate;
        if (lastUpdatedDate) {
            return new Date(lastUpdatedDate);
        }

        return null;
    }

    const getQueryMode = () => {
        const queryMode = templateInfo && templateInfo.QueryMode;

        return queryMode || null;
    };

    function evaluateXPath(context, path) {
        return xpathEngine.evaluateXPath(path, { context: context });
    }

    function getAndCacheFileAsync(fileName) {
        return api
            .getTemplateFileAsync(fileName, instanceId)
            .then(function (data) {
                // TODO: check result for errors
                const contents = data.Contents;
                templateFiles[fileName] = contents;
                return contents;
            });
    }

    const getTemplateFileAsync = async (fileName) =>
        templateFiles[fileName] || getAndCacheFileAsync(fileName);

    function getViewFileName(viewName) {
        return manifestParser.getViewFilename(viewName);
    }

    function getViewDefinitionAsync(viewName) {
        var viewFileName = getViewFileName(viewName);

        return api.requestViewAsync(viewFileName, instanceId);
    }

    function getViews() {
        return manifestParser.getViews();
    }

    function getDefaultView() {
        if (!defaultView) {
            var views = getViews();
            defaultView = views[0];
        }

        return defaultView;
    }

    // =========================
    // Rule definition retrieval
    // =========================

    // String, String -> RuleSet
    function getButtonRules(viewName, buttonName) {
        var viewRules = manifestParser.getButtonRules(viewName);

        return viewRules ? viewRules[buttonName] : null;
    }

    const isMatch = (domNode, xpath) => {
        const dom = getDocumentElement(domNode);
        const matches = evaluateXPath(dom, xpath);

        return (
            matches &&
            matches.toUnsortedArray().some(function (n) {
                return n === domNode;
            })
        );
    };

    const findRuleSetMatch = (domNode, candidates) =>
        candidates.find((c) => isMatch(domNode, c.match)) || null;

    function getRulesForField(domName, domNode) {
        const nodeName = domNode.nodeName;
        const nameToUse =
            domNode.nodeType === ATTRIBUTE_NODE ? '@' + nodeName : nodeName;
        const ruleCandidates = manifestParser.getFieldRuleCandidates(
            domName,
            nameToUse,
        );

        if (ruleCandidates) {
            return findRuleSetMatch(domNode, ruleCandidates);
        }

        return null;
    }

    function getAllValidationRules() {
        return manifestParser.getCustomValidation();
    }

    function getOnLoadRules() {
        return manifestParser.getOnLoadRules();
    }

    const getEditableComponents = (viewName) =>
        manifestParser.getEditableComponents(viewName);

    // DOMNode, String -> Array[XmlToEdit]
    const getXmlToEdits = (domNode, viewName) =>
        compose(
            filter((c) => isMatch(domNode, c.fullItem)),
            getEditableComponents,
        )(viewName);

    // =============================
    // END Rule definition retrieval
    // =============================

    // void -> DefaultValue[]
    function getAllDefaultValues() {
        return manifestParser.getAllDefaultValues();
    }

    // DomNode -> DefaultValue[]
    function getDependentDefaultValues() {
        // TODO: Make this smarter; currently just returning all default values
        return manifestParser.getAllRefDefaultValues();
    }

    function getTemplateFileName() {
        // TODO: retrieve this value from the manifest (pri 4 since it will almost always be template.xml)
        return 'template.xml';
    }

    function getEditableComponent(viewName, ecName) {
        return manifestParser.getEditableComponent(viewName, ecName);
    }

    // String -> Array[ContextMenuItem]
    function getContextMenuItems(viewName, xmlToEdit) {
        return manifestParser.getContextMenuItems(viewName, xmlToEdit);
    }

    // =================================
    // Data connection setting retrieval
    // =================================

    function getQueryAdapter(dcName) {
        return manifestParser.getQueryAdapter(dcName);
    }

    function getSubmitAdapter(dcName) {
        return manifestParser.getSubmitAdapter(dcName);
    }

    function getSubmitSettings() {
        return manifestParser.getSubmitSettings();
    }

    function getDataSourceDefinitions() {
        return manifestParser.getDataSources();
    }

    function getDataConnectionDefinitions() {
        return manifestParser.getDataConnections();
    }

    function listDataSources() {
        return manifestParser.listDataSources();
    }

    // =================================
    // END Data connection setting retrieval
    // =================================

    function getBasePath() {
        return manifestParser.getBasePath();
    }

    function getSolutionVersion() {
        return manifestParser.getSolutionVersion();
    }

    function getUpgradeSettings() {
        return manifestParser.getUpgradeSettings();
    }

    const getNamespaceManager = () => buildNamespaceManager(manifestDom);

    function getXPathEngine() {
        return xpathEngine;
    }

    function getTemplateUrlFromApi() {
        const accessType = api.getAccessType();
        const templateLocation = api.getTemplateLocation();

        switch (accessType) {
            case constants.AccessTypes.Template:
                return templateLocation;
            case constants.AccessTypes.Library:
                return (
                    templateLocation.replace(/\/+$/, '') + '/Forms/template.xsn'
                );
            default:
                return null;
        }
    }

    function getWssUrl() {
        var wssPath = manifestParser.getWssPath();

        if (wssPath) {
            return wssPath + 'Forms/template.xsn';
        }

        return null;
    }

    function getHrefUrl() {
        return (
            manifestParser.getBaseUrl() ||
            getWssUrl() ||
            manifestParser.getPublishUrl() ||
            getTemplateUrlFromApi()
        );
    }

    function getResourceAbsoluteUrl(name) {
        var urlPrefix = api.getImagePrefix(instanceId);

        // BUGBUG - Seems like += is unnecessary - should just be +
        return (urlPrefix += name);
    }

    manifestParser.setManifest(manifestDom);
    xpathEngine.nsr = getNamespaceManager();

    return {
        getTemplateFileName,
        getViewDefinitionAsync,
        getViewFileName,
        getButtonRules,
        getSubmitSettings,
        getBasePath,
        getRulesForField,
        getSubmitAdapter,
        getQueryAdapter,
        getDefaultView,
        getOnLoadRules,
        getAllDefaultValues,
        getDependentDefaultValues,
        getEditableComponent,
        getEditableComponents,
        getSolutionVersion: getSolutionVersion,
        getUpgradeSettings: getUpgradeSettings,
        getTemplateFileAsync: getTemplateFileAsync,
        getDataSourceDefinitions: getDataSourceDefinitions,
        getDataConnectionDefinitions: getDataConnectionDefinitions,
        listDataSources: listDataSources,
        getXmlToEdits,
        getContextMenuItems: getContextMenuItems,
        getNamespaceManager,
        getXPathEngine: getXPathEngine,
        getHrefUrl: getHrefUrl,
        getTemplateName: getTemplateName,
        getViews: getViews,
        getResourceAbsoluteUrl: getResourceAbsoluteUrl,
        getAllValidationRules: getAllValidationRules,
        getLastUpdatedDate,
        getQueryMode,
    };
}

async function createTemplateAsync(
    api,
    getTemplateXml,
    templateInfoSource,
    logging,
) {
    const templateInfo = await templateInfoSource.getTemplateInfoAsync();
    const instanceId = templateInfo && templateInfo.InstanceId;

    if (instanceId) {
        api.setAccessType('templateName', templateInfo.TemplateName);
    }

    try {
        logging.logVerbose('Begin get template definition');

        const templateDefinition = await api.getTemplateDefinitionAsync(
            getTemplateXml,
            instanceId,
        );

        logging.logVerbose('End get template definition');

        var files = templateDefinition.Files;

        if (files) {
            return createTemplate(api, files, instanceId, templateInfo);
        }
        if (templateDefinition.error === true) {
            throw new Error(
                'Manifest retrieval failed with the error: ' +
                    templateDefinition.message,
            );
        } else {
            console.error(templateDefinition);
            throw new Error('Could not access manifest data.');
        }
    } catch (err) {
        if (err && err.status === 404) {
            err.userDisplayMessage = buildTemplateNotFoundErrorMessage(
                api.getTemplateLocation(),
            );
        }

        throw err;
    } finally {
        util.perfMark('formsviewer_template_loaded');
    }
}

export { createTemplate, createTemplateAsync };
